import React, { useState } from 'react'
import { MoreVert as MoreVertIcon } from '@mui/icons-material'
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import EsimAPI from 'api/faber/EsimAPI'
import MUIDataGrid from 'common/AbstractDataGrid/DataGridMUI'
import format from 'date-fns/format'
import OrbitSession from 'hooks/useCookies'

import ReallocationDialog from '../dialogs/ReallocationDialog'
import ReplayAllocationDialog from '../dialogs/ReplayAllocationDialog'
import TerminateAllocationPackDialog from '../dialogs/TerminateAllocationDialog'
import UndoAllocationPackDialog from '../dialogs/UndoAllocationDialog'

interface CustomerPack {
  id: string
  catalog_id: string
  catalog_name: string
  invoice_id: string
  created_at: Date
  receiver_id: string
  receiver_email: string
}

const packsAllocationColumns = (
  onReplayAllocation: (row: CustomerPack) => void,
  onUndoAllocation: (row: CustomerPack) => void,
  onTerminateAllocation: (row: CustomerPack) => void,
  onReallocatePack: (row: CustomerPack) => void
) => [
  {
    field: 'itemName',
    headerName: 'Packs',
    sortable: false,
    flex: 1,
    minWidth: 150,
    renderCell: (params: { row: CustomerPack }) => (
      <Tooltip title={`ID: ${params.row.id}`}>
        <Typography variant='body2'>{params.row.catalog_name || ''}</Typography>
      </Tooltip>
    )
  },
  {
    field: 'created_at',
    headerName: 'Purchased',
    sortable: false,
    flex: 0.8,
    minWidth: 120,
    valueFormatter: ({ value }: { value: string }) =>
      value ? format(new Date(value), 'dd MMM yyyy HH:mm') : ''
  },
  {
    field: 'status',
    headerName: 'Status',
    sortable: false,
    flex: 0.6,
    minWidth: 100,
    renderCell: (params: { row: CustomerPack }) => {
      const isAllocated =
        params.row.receiver_id && params.row.receiver_id !== ''
      return (
        <Chip
          label={isAllocated ? 'Allocated' : 'Unallocated'}
          sx={{
            bgcolor: isAllocated ? 'success.main' : 'grey.500',
            color: '#FFFFFF',
            fontWeight: 'bold'
          }}
        />
      )
    }
  },
  {
    field: 'allocatedTo',
    headerName: 'Allocated To',
    sortable: false,
    flex: 1,
    minWidth: 120,
    renderCell: (params: { row: CustomerPack }) => (
      <Typography variant='body2'>
        {params.row.receiver_email || '—'}
      </Typography>
    )
  },
  {
    field: 'actions',
    headerName: 'Actions',
    sortable: false,
    width: 80,
    renderCell: (params: { row: CustomerPack }) => {
      const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
      const isAllocated =
        params.row.receiver_id && params.row.receiver_id !== ''
      const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
      }
      const handleClose = () => {
        setAnchorEl(null)
      }

      return (
        <>
          <IconButton onClick={handleClick}>
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            PaperProps={{
              sx: { minWidth: 150 }
            }}
          >
            {isAllocated && (
              <MenuItem
                onClick={() => {
                  onReplayAllocation(params.row)
                  handleClose()
                }}
              >
                Replay Allocation
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                if (isAllocated) {
                  onUndoAllocation(params.row)
                } else {
                  onTerminateAllocation(params.row)
                }
                handleClose()
              }}
              sx={{ color: isAllocated ? 'error.main' : 'primary.main' }}
            >
              {isAllocated ? 'Undo Allocation' : 'Terminate Allocation'}
            </MenuItem>
            {!isAllocated && (
              <MenuItem
                onClick={() => {
                  onReallocatePack(params.row)
                  handleClose()
                }}
              >
                Reallocate
              </MenuItem>
            )}
          </Menu>
        </>
      )
    }
  }
]

function PacksAllocationCard({ userId }: { userId: string }) {
  const faberToken = OrbitSession.get('faberToken')
  const theme = useTheme()
  const [dialogStates, setDialogStates] = useState({
    undoAllocation: false,
    terminateAllocation: false,
    reallocate: false,
    replayAllocate: false
  })
  const [selectedPack, setSelectedPack] = useState<CustomerPack | null>(null)
  const pageSize = 10
  const [page, setPage] = useState(0)

  const { data: customerPacks, isLoading } = useQuery(
    ['customer-allocation-packs', userId, page, pageSize],
    async () => {
      return await EsimAPI.getCustomerPacks({
        userId,
        faberToken,
        limit: pageSize,
        offset: page * pageSize
      })
    },
    { keepPreviousData: true }
  )

  const rowCount = customerPacks?.data?.body?.total || 0

  const toggleDialog = (
    dialogName: keyof typeof dialogStates,
    pack?: CustomerPack
  ) => {
    setDialogStates((prev) => ({ ...prev, [dialogName]: !prev[dialogName] }))
    if (pack) setSelectedPack(pack)
  }

  const sortedRows = customerPacks?.data?.body?.data
    ? [...customerPacks.data.body.data].sort(
        (a: CustomerPack, b: CustomerPack) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )
    : []

  return (
    <Box
      sx={{ p: { xs: 2, sm: 3 }, bgcolor: 'background.paper', borderRadius: 2 }}
    >
      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 200
          }}
        >
          <CircularProgress size={24} />
          <Typography variant='body2' sx={{ ml: 2, color: 'text.secondary' }}>
            Loading packs...
          </Typography>
        </Box>
      ) : sortedRows.length === 0 ? (
        <Typography
          variant='body2'
          sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}
        >
          No packs available
        </Typography>
      ) : (
        <MUIDataGrid
          columns={packsAllocationColumns(
            (row) => toggleDialog('replayAllocate', row),
            (row) => toggleDialog('undoAllocation', row),
            (row) => toggleDialog('terminateAllocation', row),
            (row) => toggleDialog('reallocate', row)
          )}
          rows={sortedRows}
          getRowId={(row: CustomerPack) => row.id}
          pageCount={Math.ceil(rowCount / pageSize)}
          pageNumber={page + 1}
          onPageChange={(newPageNumber) => setPage(newPageNumber - 1)}
          disableColumnMenu
          sx={{
            '& .MuiDataGrid-root': {
              border: 'none'
            },
            '& .MuiDataGrid-cell': {
              py: 1
            },
            [theme.breakpoints.down('sm')]: {
              '& .MuiDataGrid-columnHeaderTitle': {
                fontSize: '0.85rem'
              },
              '& .MuiDataGrid-cell': {
                fontSize: '0.8rem'
              }
            }
          }}
        />
      )}
      <UndoAllocationPackDialog
        allocationId={selectedPack?.id ?? ''}
        handleUndoAllocationDialogClose={() => toggleDialog('undoAllocation')}
        dialogOpen={dialogStates.undoAllocation}
        faberToken={faberToken}
      />
      <TerminateAllocationPackDialog
        allocationId={selectedPack?.id ?? ''}
        handleTerminateAllocationDialogClose={() =>
          toggleDialog('terminateAllocation')
        }
        dialogOpen={dialogStates.terminateAllocation}
        faberToken={faberToken}
      />
      <ReallocationDialog
        allocationId={selectedPack?.id ?? ''}
        handleReallocationDialogClose={() => toggleDialog('reallocate')}
        dialogOpen={dialogStates.reallocate}
        faberToken={faberToken}
      />
      <ReplayAllocationDialog
        allocationId={selectedPack?.id ?? ''}
        handleReplayAllocationDialogClose={() => toggleDialog('replayAllocate')}
        dialogOpen={dialogStates.replayAllocate}
        faberToken={faberToken}
      />
    </Box>
  )
}

export default PacksAllocationCard
